import { format as formatGregorian, parseISO } from 'date-fns';
import { format as formatJalali } from 'date-fns-jalali';
import { toPersianNumber } from './numbers';

// Persian month names
const persianMonths = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
];

/**
 * Format date in Persian (Jalali) calendar
 * @param {Date|string} date - Date to format
 * @param {string} formatStr - Format string (default: 'yyyy/MM/dd')
 * @returns {string} - Formatted Persian date
 */
export const formatPersianDate = (date, formatStr = 'yyyy/MM/dd') => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const formatted = formatJalali(dateObj, formatStr);
    return toPersianNumber(formatted);
  } catch (error) {
    console.error('Error formatting Persian date:', error);
    return '';
  }
};

/**
 * Format date in Gregorian calendar
 * @param {Date|string} date - Date to format
 * @param {string} formatStr - Format string (default: 'MMM dd, yyyy')
 * @returns {string} - Formatted Gregorian date
 */
export const formatEnglishDate = (date, formatStr = 'MMM dd, yyyy') => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatGregorian(dateObj, formatStr);
  } catch (error) {
    console.error('Error formatting English date:', error);
    return '';
  }
};

/**
 * Format date based on language
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code ('en' or 'fa')
 * @param {string} formatStr - Optional custom format string
 * @returns {string} - Formatted date
 */
export const formatDate = (date, language = 'fa', formatStr = null) => {
  if (!date) return '';

  if (language === 'fa') {
    return formatPersianDate(date, formatStr || 'yyyy/MM/dd');
  }

  return formatEnglishDate(date, formatStr || 'MMM dd, yyyy');
};

/**
 * Format date with time
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code ('en' or 'fa')
 * @returns {string} - Formatted date with time
 */
export const formatDateTime = (date, language = 'fa') => {
  if (!date) return '';

  if (language === 'fa') {
    const datePart = formatPersianDate(date, 'yyyy/MM/dd');
    const timePart = formatPersianDate(date, 'HH:mm');
    return `${datePart} - ${timePart}`;
  }

  return formatEnglishDate(date, 'MMM dd, yyyy - HH:mm');
};

/**
 * Format date in long format
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code ('en' or 'fa')
 * @returns {string} - Formatted date in long format
 */
export const formatLongDate = (date, language = 'fa') => {
  if (!date) return '';

  if (language === 'fa') {
    return formatPersianDate(date, 'dd MMMM yyyy');
  }

  return formatEnglishDate(date, 'MMMM dd, yyyy');
};

/**
 * Get relative time (e.g., "2 hours ago")
 * @param {Date|string} date - Date to format
 * @param {string} language - Language code ('en' or 'fa')
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (date, language = 'fa') => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor((now - dateObj) / 1000);

    if (diffInSeconds < 60) {
      return language === 'fa'
        ? `${toPersianNumber(diffInSeconds)} ثانیه پیش`
        : `${diffInSeconds} seconds ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return language === 'fa'
        ? `${toPersianNumber(diffInMinutes)} دقیقه پیش`
        : `${diffInMinutes} minutes ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return language === 'fa'
        ? `${toPersianNumber(diffInHours)} ساعت پیش`
        : `${diffInHours} hours ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return language === 'fa'
        ? `${toPersianNumber(diffInDays)} روز پیش`
        : `${diffInDays} days ago`;
    }

    // For older dates, return formatted date
    return formatDate(date, language);
  } catch (error) {
    console.error('Error getting relative time:', error);
    return '';
  }
};

export default {
  formatPersianDate,
  formatEnglishDate,
  formatDate,
  formatDateTime,
  formatLongDate,
  getRelativeTime
};
